import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon, Phone } from "lucide-react";
import { HOTEL, NAV_LINKS } from "../data/site";
import { useTheme } from "../hooks/useTheme";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggle } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-navbar shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between py-4">
        <a
          href="#home"
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
          aria-label="Maa Durga Palace home"
        >
          <span className="relative inline-flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-full overflow-hidden shadow-lg shadow-[#7A1A24]/30 ring-1 ring-[#D4AF37]/50 bg-[#7A1A24]">
            <img
              src={HOTEL.logo}
              alt="Maa Durga Palace & Guest House logo"
              width="48"
              height="48"
              loading="eager"
              decoding="async"
              className="h-full w-full object-cover"
            />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-display text-base sm:text-lg font-semibold text-foreground">
              Maa Durga Palace
            </span>
            <span className="text-[10px] sm:text-xs tracking-[0.25em] uppercase text-[#D4AF37]">
              & Guest House
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-full hover:bg-foreground/5 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="theme-toggle-btn"
            aria-label="Toggle dark mode"
            className="h-10 w-10 rounded-full grid place-items-center text-foreground hover:bg-foreground/5 transition-colors"
          >
            <AnimatePresence mode="wait" initial={false}>
              {theme === "dark" ? (
                <motion.span key="sun" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.25 }}>
                  <Sun size={18} />
                </motion.span>
              ) : (
                <motion.span key="moon" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.25 }}>
                  <Moon size={18} />
                </motion.span>
              )}
            </AnimatePresence>
          </button>

          <a
            href={HOTEL.whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-book-btn"
            className="hidden sm:inline-flex btn-primary !py-2.5 !px-5 text-xs"
          >
            <Phone size={14} />
            Book Venue
          </a>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="navbar-mobile-toggle"
            aria-label="Toggle navigation"
            className="lg:hidden h-10 w-10 rounded-full grid place-items-center hover:bg-foreground/5"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="navbar-mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="lg:hidden glass-navbar border-t border-border"
          >
            <div className="container-x py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={close}
                  className="py-2.5 px-3 rounded-lg text-sm text-muted-foreground hover:bg-foreground/5"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={HOTEL.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary mt-2"
              >
                Book Wedding Venue
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
